import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Plus, Users, X } from 'lucide-react';
import { MEMBERS_PAGE_SIZE } from '@ready/shared';
import { useApi, type Group, type Member } from '@/lib/api';
import { useDebounced } from '@/lib/use-debounced';
import { Button, Card, ErrorText, Field, Input, PageHeader } from '@/components/ui';
import { SearchBox } from '@/components/catalog';
import { Pager } from '@/components/people';
import { useOrgContext } from './layout';

/*
 * Groups split an org's members (a batch, a department, a team) so admins
 * can assign courses to them, invite people straight into them, and filter
 * members by them. Admins of a parent org see them read-only.
 */

const plural = (n: number, word: string) =>
  `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

export function OrgGroupsPage() {
  const { org, canManage, role } = useOrgContext();
  const isAdminView = canManage || role === null;
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const groups = useQuery({
    queryKey: ['groups', org.id],
    queryFn: () => api.groups(org.id),
    enabled: isAdminView,
  });
  const create = useMutation({
    mutationFn: () =>
      api.createGroup(org.id, { name: name.trim(), description: description.trim() || null }),
    onSuccess: () => {
      setName('');
      setDescription('');
      queryClient.invalidateQueries({ queryKey: ['groups', org.id] });
    },
  });
  if (!isAdminView) return <Navigate to=".." replace />;

  return (
    <div className="space-y-6">
      <p className="max-w-3xl text-sm text-muted-foreground">
        Groups split your members into batches, departments or teams. Assign courses to a group,
        invite people straight into one, and filter members by group.
      </p>
      {canManage && (
        <Card title="New group">
          <form
            className="max-w-3xl space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <Field label="Name" htmlFor="group-name">
              <Input
                id="group-name"
                value={name}
                maxLength={80}
                placeholder="e.g. CS Batch 2026, Finance team"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Description (optional)" htmlFor="group-description">
              <Input
                id="group-description"
                value={description}
                maxLength={300}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <Button
              type="submit"
              className="w-full sm:w-auto"
              disabled={!name.trim() || create.isPending}
            >
              <Plus className="size-4" aria-hidden /> Create group
            </Button>
            <ErrorText error={create.error} />
          </form>
        </Card>
      )}

      <Card title="Groups">
        {groups.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : groups.isError ? (
          <ErrorText error={groups.error} />
        ) : groups.data.groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No groups yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {groups.data.groups.map((g) => (
              <li key={g.id}>
                <Link
                  to={g.id}
                  className="flex items-center justify-between gap-3 py-3 hover:text-primary"
                >
                  <span className="min-w-0">
                    <span className="block font-medium">{g.name}</span>
                    {g.description && (
                      <span className="block text-sm text-muted-foreground">{g.description}</span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
                    <Users className="size-4" aria-hidden /> {plural(g.members, 'member')}
                    <ChevronRight className="size-4" aria-hidden />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function OrgGroupPage() {
  const { org, canManage, role } = useOrgContext();
  const isAdminView = canManage || role === null;
  const { groupId = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const groups = useQuery({
    queryKey: ['groups', org.id],
    queryFn: () => api.groups(org.id),
    enabled: isAdminView,
  });
  const group = groups.data?.groups.find((g) => g.id === groupId);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['groups', org.id] });
    queryClient.invalidateQueries({ queryKey: ['members', org.id] });
  };
  const remove = useMutation({
    mutationFn: () => api.deleteGroup(org.id, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups', org.id] });
      navigate('..', { relative: 'path' });
    },
  });

  if (!isAdminView) return <Navigate to="../.." relative="path" replace />;
  if (groups.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!group)
    return (
      <Card>
        <p className="text-sm text-muted-foreground">This group doesn&rsquo;t exist anymore.</p>
        <Link to=".." relative="path" className="mt-2 inline-block text-sm text-primary">
          Back to groups
        </Link>
      </Card>
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title={group.name}
        description={group.description ?? undefined}
        back={{ to: `/organizations/${org.id}/groups`, label: 'Groups' }}
        action={
          canManage && (
            <Button
              variant="ghost"
              disabled={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Delete ${group.name}? Its members stay in ${org.name}; courses assigned only to this group stop reaching anyone.`,
                  )
                )
                  remove.mutate();
              }}
            >
              Delete group
            </Button>
          )
        }
      />
      <ErrorText error={remove.error} />
      {canManage && <EditGroup group={group} onSaved={refresh} />}
      <GroupMembers group={group} canManage={canManage} onChange={refresh} />
      {canManage && <AddMembers group={group} onAdded={refresh} />}
    </div>
  );
}

function EditGroup({ group, onSaved }: { group: Group; onSaved: () => void }) {
  const { org } = useOrgContext();
  const api = useApi();
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.updateGroup(org.id, group.id, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: onSaved,
  });
  const changed = name.trim() !== group.name || description.trim() !== (group.description ?? '');
  return (
    <Card title="Details">
      <form
        className="max-w-3xl space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (changed && name.trim()) save.mutate();
        }}
      >
        <Field label="Name" htmlFor="edit-group-name">
          <Input
            id="edit-group-name"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Description (optional)" htmlFor="edit-group-description">
          <Input
            id="edit-group-description"
            value={description}
            maxLength={300}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Button
          type="submit"
          variant="secondary"
          className="w-full sm:w-auto"
          disabled={!changed || !name.trim() || save.isPending}
        >
          Save changes
        </Button>
        <ErrorText error={save.error} />
      </form>
    </Card>
  );
}

const nameOf = (m: Member) => m.displayName ?? m.email;

function GroupMembers({
  group,
  canManage,
  onChange,
}: {
  group: Group;
  canManage: boolean;
  onChange: () => void;
}) {
  const { org } = useOrgContext();
  const api = useApi();
  const [page, setPage] = useState(0);
  const query = {
    groupId: group.id,
    sort: 'name',
    offset: page * MEMBERS_PAGE_SIZE,
    limit: MEMBERS_PAGE_SIZE,
  };
  const members = useQuery({
    queryKey: ['members', org.id, query],
    queryFn: () => api.members(org.id, query),
    placeholderData: keepPreviousData,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.removeGroupMember(org.id, group.id, userId),
    onSuccess: onChange,
  });
  return (
    <Card title={`Members (${group.members.toLocaleString('en-US')})`}>
      {members.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : members.isError ? (
        <ErrorText error={members.error} />
      ) : members.data.members.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No one in this group yet.
          {canManage && ' Add members below, or invite people straight into it.'}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {members.data.members.map((m) => (
            <li key={m.userId} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <Link
                to={`/organizations/${org.id}/members/${m.userId}`}
                className="min-w-0 hover:text-primary"
              >
                <span className="block break-all font-medium">{nameOf(m)}</span>
                {m.displayName && (
                  <span className="block break-all text-xs text-muted-foreground">{m.email}</span>
                )}
              </Link>
              {canManage && (
                <Button
                  variant="ghost"
                  aria-label={`Remove ${nameOf(m)} from ${group.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(m.userId)}
                >
                  <X className="size-4" aria-hidden /> Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ErrorText error={remove.error} />
      <Pager
        page={page}
        pageSize={MEMBERS_PAGE_SIZE}
        total={members.data?.total ?? 0}
        busy={members.isFetching}
        onPage={setPage}
      />
    </Card>
  );
}

/** Finds the org's members by name or email and adds the chosen ones. */
function AddMembers({ group, onAdded }: { group: Group; onAdded: () => void }) {
  const { org } = useOrgContext();
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  const query = { q, sort: 'name', limit: 20 };
  const results = useQuery({
    queryKey: ['members', org.id, query],
    queryFn: () => api.members(org.id, query),
    enabled: q.length > 0,
    placeholderData: keepPreviousData,
  });
  const add = useMutation({
    mutationFn: () => api.addGroupMembers(org.id, group.id, [...picked.keys()]),
    onSuccess: () => {
      setPicked(new Map());
      setText('');
      onAdded();
    },
  });
  const toggle = (m: Member) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(m.userId)) next.delete(m.userId);
      else next.set(m.userId, nameOf(m));
      return next;
    });

  return (
    <Card title="Add members">
      <div className="space-y-3">
        <SearchBox
          value={text}
          onChange={setText}
          placeholder="Find members by name or email"
          label="Find members to add"
        />
        {q && results.data && (
          <ul className="divide-y divide-border">
            {results.data.members.length === 0 && (
              <li className="py-2 text-sm text-muted-foreground">No members match.</li>
            )}
            {results.data.members.map((m) => {
              const already = m.groups.includes(group.name);
              return (
                <li key={m.userId}>
                  <label className="flex cursor-pointer items-center gap-3 py-2.5 text-sm">
                    <input
                      type="checkbox"
                      className="size-5 accent-primary"
                      disabled={already}
                      checked={already || picked.has(m.userId)}
                      onChange={() => toggle(m)}
                    />
                    <span className="min-w-0">
                      <span className="block break-all font-medium">{nameOf(m)}</span>
                      <span className="block break-all text-xs text-muted-foreground">
                        {already ? 'Already in this group' : m.email}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {picked.size > 0 && (
          <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {[...picked.values()].slice(0, 3).join(', ')}
              {picked.size > 3 ? ` and ${picked.size - 3} more` : ''}
            </p>
            <Button
              className="w-full sm:w-auto"
              disabled={add.isPending}
              onClick={() => add.mutate()}
            >
              Add {plural(picked.size, 'member')}
            </Button>
          </div>
        )}
        <ErrorText error={add.error} />
      </div>
    </Card>
  );
}
